import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * The side panel's URL state (slice 1 spec §7.1): `?panel=open`,
 * `?panel=<threadId>`, or none for the collapsed rail. Setting it pushes a
 * history entry, so Back and Forward walk the panel states (`replace` swaps the
 * current one instead). Null closes it.
 */
export function usePanel() {
  const [params, setParams] = useSearchParams();
  const panel = params.get("panel");
  const setPanel = useCallback(
    (value: string | null, opts?: { replace?: boolean }) =>
      setParams((prev) => withPanel(prev, value), opts),
    [setParams],
  );
  return [panel, setPanel] as const;
}

/**
 * The query string with only `panel` changed; the page's own parameters stay.
 * A doc attached by Ask to change (`attach`) stays with the chat it opened,
 * so moving the panel drops it.
 */
export function withPanel(search: URLSearchParams | string, value: string | null) {
  const next = new URLSearchParams(search);
  next.delete("attach");
  if (value) next.set("panel", value);
  else next.delete("panel");
  return next;
}

/** A link to open `threadId` in the panel from the current page. */
export function panelHref(search: string, threadId: string): string {
  return `?${withPanel(search, threadId).toString()}`;
}

/** Where "your last thread" is kept: one per workspace, in this browser. */
type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const lastKey = (workspaceId: string) => `crew:panel:last:${workspaceId}`;

/** The browser's storage, or none where it is blocked (a private window can throw). */
function browserStore(): Store | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Remember the thread the panel shows, so reopening it goes back there. Returns the
 * thread kept before it (null when none, or the same), for `forgetThread` to put back.
 */
export function rememberThread(
  workspaceId: string,
  threadId: string,
  store = browserStore(),
): string | null {
  try {
    const before = store?.getItem(lastKey(workspaceId)) ?? null;
    store?.setItem(lastKey(workspaceId), threadId);
    return before === threadId ? null : before;
  } catch {
    // A full or blocked store just means the panel opens on a new thread.
    return null;
  }
}

/**
 * Forget `threadId` once it can't be loaded, if it's still the one kept, putting back
 * the thread kept before it: a broken link leaves the last good thread in place.
 */
export function forgetThread(
  workspaceId: string,
  threadId: string,
  before: string | null = null,
  store = browserStore(),
) {
  try {
    if (store?.getItem(lastKey(workspaceId)) !== threadId) return;
    if (before) store.setItem(lastKey(workspaceId), before);
    else store.removeItem(lastKey(workspaceId));
  } catch {
    // As above.
  }
}

/** What opening the panel shows: the last thread, else a new one (`open`). */
export function resumePanel(workspaceId: string, store = browserStore()): string {
  try {
    return store?.getItem(lastKey(workspaceId)) || "open";
  } catch {
    return "open";
  }
}
